import api from 'src/utils/ajax';
import { decamelize } from 'src/utils/decamelize';

import { FranchiseFeeLineItem } from './_types';
import { normalizeFranchiseFeeLineItem } from './normalizers';

export * from './_types';

export const FranchiseFeeLineItemsQueryKey = 'franchise-fee-line-items';

export const fetchFranchiseFeeLineItemsQueryKey = (
  params?: FranchiseFeeLineItemsParams
) => [FranchiseFeeLineItemsQueryKey, params];

export interface FranchiseFeeLineItemsParams {
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
  search?: string;
  filters?: {
    lineType?: string;
    createdAt?: {
      start: string;
      end: string;
    };
    franchiseFeeId?: string;
    sites?: string;
    payoutId?: string;
  };
}

export const fetchFranchiseFeeLineItems = async ({
  sortBy,
  sortDir,
  page = 0,
  perPage = 20,
  search = '',
  filters = {},
}: FranchiseFeeLineItemsParams) => {
  const params = {
    page: page,
    per: perPage,
    ...(search && { 'q[item_name_cont]': search }),
    ...(sortBy && {
      'q[s]': `${decamelize(sortBy || '')} ${sortDir || 'desc'}`,
    }),
    ...(filters.lineType && { 'q[line_type_eq]': filters.lineType }),
    ...(filters.franchiseFeeId && {
      'q[franchise_fee_id_eq]': filters.franchiseFeeId,
    }),
    ...(filters.createdAt && {
      'q[created_at_gteq]': filters.createdAt.start,
      'q[created_at_lteq]': filters.createdAt.end,
    }),
    ...(filters.sites && {
      site_ids: filters.sites,
    }),
    ...(filters.payoutId && {
      'q[franchise_fee_payout_id_eq]': filters.payoutId,
    }),
  };
  const { data } = await api.get('/api/v4/hq/franchise_fee_line_items', {
    params,
  });

  // Handle both paginated and non-paginated responses
  if (data.franchise_fee_line_items && data.meta?.total !== undefined) {
    // Paginated response
    return {
      franchiseFeeLineItems: data.franchise_fee_line_items.map(
        normalizeFranchiseFeeLineItem
      ),
      total: data.meta.total,
    };
  } else if (Array.isArray(data)) {
    // Non-paginated response (fallback)
    return {
      franchiseFeeLineItems: data.map(normalizeFranchiseFeeLineItem),
      total: data.length,
    };
  } else {
    // Handle other response formats
    return {
      franchiseFeeLineItems: [],
      total: 0,
    };
  }
};

export const fetchFranchiseFeeLineItem = async (
  id: string
): Promise<FranchiseFeeLineItem> => {
  const { data } = await api.get(`/api/v4/hq/franchise_fee_line_items/${id}`);
  return normalizeFranchiseFeeLineItem(data);
};
